import { apiClient } from './api';

// Types
export interface Organization {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  website?: string | null;
  logo_url?: string | null;
  settings?: Record<string, unknown> | null;
  /** REV-798: preferred language (a @contract/locales code), or null for none. */
  locale?: string | null;
  /** REV-1127: false switches every AI feature off for the organization. */
  ai_enabled?: boolean;
  status: 'active' | 'suspended' | 'deleted';
  created_at: string;
  updated_at: string;
}

export interface OrganizationMember {
  id: string;
  user_id: string;
  organization_id: string;
  role: 'owner' | 'admin' | 'manager' | 'member' | 'viewer';
  status: 'active' | 'suspended';
  joined_at: string;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
  };
}

export interface Role {
  id: string;
  slug: string;
  name: string;
  description: string;
  type: string;
  hierarchy_level: number;
}

// Mirrors the OrganizationInvitation status enum: cancelling calls .revoke(),
// so the terminal value is 'revoked' — there is no 'cancelled'.
type InvitationStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

/**
 * A pending invitation as GET /organizations/:orgId/invites returns it
 * (invitationService.listInvitations).
 *
 * REV-524: `role` is an OBJECT here — and nullable — not a slug string. The
 * previous `role: string` made `t(\`organizations.roles.${inv.role}\`)`
 * stringify the object into the key `organizations.roles.[object Object]`,
 * which rendered raw in the Members table. This endpoint also does NOT return
 * `organization_id` or `invited_by` (it returns a composed `inviter` object),
 * so those are gone too rather than being permanently undefined.
 */
export interface Invitation {
  id: string;
  email: string;
  role: { slug: string; name: string } | null;
  inviter: { id: string; email: string; name: string } | null;
  status: InvitationStatus;
  expires_at: string;
  created_at: string;
}

/**
 * What POST /organizations/:orgId/invites returns. Deliberately a separate
 * type: unlike the list endpoint this returns `role` as a SLUG STRING and does
 * include `organization_id`. One interface cannot honestly describe both
 * (REV-524).
 */
export interface CreatedInvitation {
  id: string;
  organization_id: string;
  email: string;
  role: string;
  status: InvitationStatus;
  expires_at: string;
  created_at: string;
}

/**
 * What GET /organizations/slug/:slug returns: the organization PLUS the
 * caller's own role in it (organizationService.getBySlug adds
 * `role: membership.role.slug`). Callers need the role to decide which
 * mutating actions to offer (REV-523).
 */
export interface OrganizationWithRole extends Organization {
  role: string;
}

export interface CreateOrganizationData {
  name: string;
  slug: string;
  description?: string;
  website?: string;
  settings?: Record<string, unknown>;
}

export interface UpdateOrganizationData {
  name?: string;
  /** null clears it (REV-956). */
  description?: string | null;
  website?: string | null;
  logo_url?: string | null;
  settings?: Record<string, unknown>;
  /** REV-1127: the organization AI switch (Owner/Admin). */
  ai_enabled?: boolean;
}

export interface CreateInvitationData {
  email: string;
  role: 'admin' | 'manager' | 'member' | 'viewer';
  team_ids?: string[];
}

/**
 * What POST /organizations/:orgId/invites/:id/resend actually returns
 * (invitationService.resend) — a 3-field acknowledgement, not a full Invitation.
 */
export interface ResendInvitationResult {
  id: string;
  email: string;
  expires_at: string;
}

/**
 * What PUT /organizations/:orgId/members/:userId/role actually returns
 * (roleService.assignRole) — a role-change summary, not a member record.
 */
export interface RoleAssignmentResult {
  user_id: string;
  organization_id: string;
  role: string;
  previous_role: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  requestId?: string;
}

interface PaginatedResponse<T> {
  success: boolean;
  data: {
    items: T[];
    total: number;
    limit: number;
    offset: number;
  };
  requestId?: string;
}

// Organization API
export const organizationService = {
  // List user's organizations
  list: async (params?: { limit?: number; offset?: number }) => {
    const response = await apiClient.get<PaginatedResponse<Organization>>(
      '/v1/organizations',
      params
    );
    return response.data;
  },

  // Get organization by ID
  getById: async (id: string) => {
    const response = await apiClient.get<ApiResponse<{ organization: Organization }>>(
      `/v1/organizations/${id}`
    );
    return response.data.organization;
  },

  // Get organization by slug
  getBySlug: async (slug: string) => {
    const response = await apiClient.get<ApiResponse<{ organization: OrganizationWithRole }>>(
      `/v1/organizations/slug/${slug}`
    );
    return response.data.organization;
  },

  // Create organization
  create: async (data: CreateOrganizationData) => {
    const response = await apiClient.post<ApiResponse<{ organization: Organization }>>(
      '/v1/organizations',
      data
    );
    return response.data.organization;
  },

  // Update organization
  update: async (id: string, data: UpdateOrganizationData) => {
    const response = await apiClient.put<ApiResponse<{ organization: Organization }>>(
      `/v1/organizations/${id}`,
      data
    );
    return response.data.organization;
  },

  // Delete organization
  delete: async (id: string) => {
    await apiClient.delete(`/v1/organizations/${id}`);
  },

  // Check slug availability
  checkSlug: async (slug: string) => {
    const response = await apiClient.get<ApiResponse<{ available: boolean }>>(
      `/v1/organizations/check-slug/${slug}`
    );
    return response.data;
  },

  // Get organization roles
  getRoles: async (orgId: string) => {
    const response = await apiClient.get<ApiResponse<{ roles: Role[] }>>(
      `/v1/organizations/${orgId}/roles`
    );
    return response.data.roles;
  },

  // Get organization members
  getMembers: async (orgId: string, params?: { limit?: number; offset?: number }) => {
    const response = await apiClient.get<PaginatedResponse<OrganizationMember>>(
      `/v1/organizations/${orgId}/members`,
      params
    );
    return response.data;
  },

  /**
   * Assign a role to an org member.
   *
   * Returns a role-change summary, NOT an OrganizationMember: roleController
   * responds `data: result` (flat) where roleService.assignRole returns
   * { user_id, organization_id, role, previous_role }. This previously read
   * `response.data.member`, which was always undefined (REV-520).
   */
  assignRole: async (orgId: string, userId: string, role: string) => {
    const response = await apiClient.put<ApiResponse<RoleAssignmentResult>>(
      `/v1/organizations/${orgId}/members/${userId}/role`,
      { role }
    );
    return response.data;
  },

  // Remove member
  removeMember: async (orgId: string, userId: string) => {
    await apiClient.delete(`/v1/organizations/${orgId}/members/${userId}`);
  },

  // Deactivate member
  deactivateMember: async (orgId: string, userId: string) => {
    await apiClient.post(`/v1/organizations/${orgId}/members/${userId}/deactivate`);
  },

  // Reactivate member
  reactivateMember: async (orgId: string, userId: string) => {
    await apiClient.post(`/v1/organizations/${orgId}/members/${userId}/reactivate`);
  },

  // Transfer ownership
  transferOwnership: async (orgId: string, newOwnerId: string) => {
    await apiClient.post(`/v1/organizations/${orgId}/transfer-ownership`, {
      new_owner_id: newOwnerId,
    });
  },

  // Invitations
  getInvitations: async (orgId: string, params?: { limit?: number; offset?: number }) => {
    const response = await apiClient.get<PaginatedResponse<Invitation>>(
      `/v1/organizations/${orgId}/invites`,
      params
    );
    return response.data;
  },

  // invitationController returns the invitation flat in `data`, not wrapped as
  // `data.invitation` — this read undefined before REV-520.
  createInvitation: async (orgId: string, data: CreateInvitationData) => {
    const response = await apiClient.post<ApiResponse<CreatedInvitation>>(
      `/v1/organizations/${orgId}/invites`,
      data
    );
    return response.data;
  },

  cancelInvitation: async (orgId: string, invitationId: string) => {
    await apiClient.delete(`/v1/organizations/${orgId}/invites/${invitationId}`);
  },

  // Same flat `data` envelope as createInvitation. Returns only
  // { id, email, expires_at } — not a full Invitation (REV-520).
  resendInvitation: async (orgId: string, invitationId: string) => {
    const response = await apiClient.post<ApiResponse<ResendInvitationResult>>(
      `/v1/organizations/${orgId}/invites/${invitationId}/resend`
    );
    return response.data;
  },
};

export default organizationService;
